import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores'
import { useQuasar } from 'quasar'

// Its own key rather than the brand page's `infoPanelIsActive`, whose meaning (open vs. collapsed)
// is inconsistent across its call sites.
const SETTING_KEY = 'watchlistAsideCollapsed'

/**
 * Open/collapsed state of the watchlist page's criteria aside.
 *
 * From `md` up the aside is a column beside the content, and its collapsed state is a user
 * setting (localStorage + synced to the account), so it survives reloads and devices.
 * Below `md` it becomes a sheet over the content instead: always closed on arrival and never
 * persisted, so a phone never overwrites the desktop preference.
 */
export const useWatchlistAside = () => {
  const userStore = useUserStore()
  const { settings } = storeToRefs(userStore)
  const $q = useQuasar()

  const isOverlayOpen = ref(false)

  const isOverlay = computed(() => $q.screen.lt.md)
  const isAsideCollapsed = computed(() => isOverlay.value ? !isOverlayOpen.value : !!settings.value?.[SETTING_KEY])

  const setAsideCollapsed = collapsed => {
    if (isOverlay.value) isOverlayOpen.value = !collapsed
    else if (collapsed !== isAsideCollapsed.value) userStore.updateSetting(SETTING_KEY, collapsed)
  }
  const toggleAside = () => setAsideCollapsed(!isAsideCollapsed.value)
  const openAside = () => setAsideCollapsed(false)
  const closeAside = () => setAsideCollapsed(true)

  // Crossing the breakpoint either way closes the sheet, so it never covers content on arrival.
  watch(isOverlay, () => {
    isOverlayOpen.value = false
  })

  return { isAsideCollapsed, isOverlay, toggleAside, openAside, closeAside }
}
